import React from "react";
import OnePanel from "./OnePanel";
import Styled, { LineBreak, Exhibit } from "./styles";
import styledComponents from 'styled-components';

import DistributionCenters from "../../containers/assets/distribution-centers.png";

const Name = styledComponents.span`
  color: #ff5252;
  font-weight: bold;
`;

const Image = styledComponents.img`
  width: 600px;
  float: right;
  margin-left: 50px;
`;

const calculate = constants => constants.holding_rate * 100;

const Operations = ({ constants, activeRound }) => {
  const unsoldInventoryValue = 100;
  const holdingCost = (unsoldInventoryValue * constants.holding_rate);

  return (
    <OnePanel title="THE OPERATIONS">
      Jose with his materials science degree and experience in semiconductor manufacturing understood
      the science and technical details behind the manufacturing process. Through his industry connections,
      he was also able to narrow down a handful of idling fabs. After intense negotiations, the team was able
      to secure a fab for $1M.
      <LineBreak />
      In order to start operating the fab, Team Z will have to purchase production lines. Jose worked closely with
      Bob, who’s degree is in industrial engineering, to explore the production line options. They identified four
      options. Each product line has an estimated useful life of eight years. Just like every other semiconductor fab,
      this fab is expected to operate 24/7; therefore, the annual capacity of the fab (as well as each product line)
      will be 8,736 hours. In the semiconductor industry, it is customary to allow 25% of the full annual capacity for
      machine set-up, maintenance and other downtimes. Therefore, the practical utilization of each machine is 75% of
      the full capacity. If needed, machines can be operated close to 100% capacity, but that would increase machine
      wear and tear and therefore result in maintenance cost (around $200K max of additional cost). <Exhibit number={4} round={activeRound} />
      {" "}summarizes the initial investment, output per hour and the set-up time (time required to get the machine to
      operating condition) for each production line.
      <LineBreak />
      Anna took a careful look at the production line information.
      <LineBreak />
      <Name>Anna</Name>: "Oh man!!! These are expensive machines; Do these have a good resale value?"
      <LineBreak />
      <Name>Jose</Name>: "Well there is a second-hand market for sure. However, I can’t say that the resale value is good. If you think
      about it, semiconductor technology changes fast; so better machines come to market almost every year."
      <LineBreak />
      <Name>Bob</Name>: "So, let’s say we want to sell a machine after using it for one year. How much can we recover?"
      <LineBreak />
      <Name>Jose</Name>: "If you sell a production line, you'll take a loss. The loss is 5-10% of the machine's book value,
      which is what you originally paid minus the value it's already lost to depreciation. The longer you've owned the machine,
      the bigger the loss. Selling after three years, for example, usually means a 10% loss."
      <LineBreak />
      <Name>Anna</Name>: "I have another question. Since there is a large unmet demand, should we plan to do production scheduling to produce as much as we can?"
      <LineBreak />
      <Name>Jose</Name>: "Anna, you have to be very careful here. I heard that typical inventory carrying costs for image sensors are about {calculate(constants)}%."
      <LineBreak />
      <Name>Anna</Name>: "What does that mean?"
      <LineBreak />
      <Name>William</Name>: "Oh I know the answer to that one. Say the value of unsold inventory at the year end is ${unsoldInventoryValue}. It will cost you ${holdingCost} to hold that
      inventory, due to storage, insurance, finance costs etc."
      <LineBreak />
      <Name>Anna</Name>: "Wow!!! That means we have to be very careful in making our product line decisions and our production volume decisions."
      <LineBreak />
      <Name>Teresa</Name>: "Yes and yes!!! but really, we have to be very careful with all of our decisions. So each one of us should have an understanding
      related to all our business decisions."
      <LineBreak />
      <Name>Bob</Name>: "If we have unsold inventory at the end of year 1, can we sell it next year, or do we need to drop the price?"
      <LineBreak />
      <Name>Jose</Name>: "It depends on why it didn't sell. If it's pricing, we can adjust and try again. If it's quality, or a
      competitor had a better product, no price cut will help. We can upgrade quality, but that means extra R&amp;D spend."
      <LineBreak />
      <Name>Anna</Name>: "Jose and Bob, you two know manufacturing best. Could you compile some basic info on image sensor production for us?"
      <LineBreak />
      Jose and Bob compiled the following information related to product costs. Product cost is based on three manufacturing resources: direct
      materials, direct labor, and manufacturing overhead. Product cost per unit is needed to calculate the gross profit and gross margin of
      each product.
      <LineBreak />
      <Styled.UnmarkedList>
        <li>
          <Styled.Bold>Direct Materials</Styled.Bold>: Materials used to produce the image sensors (e.g. silicon wafers). This cost
          is roughly 60-85% of total product cost. Direct material cost depends on the product design (type and quantity of materials required)
          and the supplier pricing. Direct materials cost variation based on the design is due to the differences in product features. Based on
          information from competitor analysis, materials cost per sensor based on different product feature ranks are provided in
          {" "}<Exhibit number={3} round={activeRound} />.
        </li>
        <li>
          <Styled.Bold>Direct Labor Costs</Styled.Bold>: These are production employee wages. These employees are paid at an hourly rate of
          $20 per hour. Direct labor hours are driven by production run hours (i.e. how long do the machines operate per period). It takes
          at least two employees to manage one production line and no employees can work more than two daily shifts (i.e. 16 hrs per day).
          1 daily shift = 8hrs. Employees receive overtime pay at a rate of time and a half e for any hours they work over 40 hours per week.
          Using efficient machines (machines that produce more units per hour) reduces the need for labor hours; thereby reduces the labor cost.
        </li>
        <li>
          <Styled.Bold>Manufacturing Overhead Costs</Styled.Bold>: All manufacturing costs except for direct materials and direct labor costs are
          categorized as manufacturing overhead cost. A large portion of overhead cost is machine depreciation (i.e. depreciation of production
          lines). Manufacturing overhead cost is the second largest component of the product cost. We use the unit of production method to depreciate
          equipment. Manufacturing overhead costs are allocated to the individual products.
        </li>
      </Styled.UnmarkedList>
      Anna, a business major, looked concerned. "Do we have to calculate direct materials, labor, and overhead costs to get unit cost per product?"
      <LineBreak />
      <Name>William</Name>: "Maybe we hire an accountant."
      <LineBreak />
      <Name>Bob</Name>: "No need. Jose set us up with a program that calculates unit cost once we input product features, volumes, and production line info."
      <LineBreak />
      <Name>Anna</Name>: "Jose, I’ve been thinking… How are we going to get our products to customers quickly once we start manufacturing?"
      <LineBreak />
      <Name>Jose</Name>: "Good question. We should consider using a distribution center."
      <Name>Anna</Name>: "A distribution center? Like a warehouse?"
      <LineBreak />
      <Name>Jose</Name>: "Sort of, but more than that. A distribution center is where products are received, stored briefly, and then shipped out efficiently to customers. It helps reduce lead time."
      <LineBreak />
      <Image src={DistributionCenters} alt="Distribution center options" />

      <Name>Anna</Name>: "Lead time? What’s that?"
      <LineBreak />
      <Name>Jose</Name>: "It’s the time between when a customer places an order and when they receive the product. The shorter the lead time, the better. Customers don’t like waiting."
      <LineBreak />
      <Name>Anna</Name>: "Oh, so having a distribution center means we can ship products faster?"
      <LineBreak />
      <Name>Jose</Name>: "Exactly. If we place it strategically, we can improve delivery speed. We have three location options: Arizona, Arkansas, and Ohio. Each has advantages in terms of customer proximity."
      <LineBreak />
      <Name>Anna</Name>: "That makes sense. If we’re closer to customers, we can move inventory faster and avoid stockpiling too much."
      <LineBreak />
      <Name>Jose</Name>: "Right. Less inventory holding means lower costs and fewer risks. Choosing the right location could give us a big operational and customer advantage."
      <LineBreak />
      <Name>Anna</Name>: "Good point! To be cautious, let's start with a manufacturing facility first, see how it goes, and then consider adding a distribution center. If we do, we'll make sure to pick the best location."
      <LineBreak />
    </OnePanel>
  )
};

export default Operations;



// WEBPACK FOOTER //
// ./src/components/bcase/Operations.js